/**
 * useSiteSettings(key)
 * ---------------------
 * Fetches a public settings group from /api/site-settings/public/:key
 * and caches the result in a module-level map for the session.
 *
 * Usage:
 *   const { data: contact, loading } = useSiteSettings('contact');
 *   const { data: social,  loading } = useSiteSettings('social');
 *   const { data: legal,   loading } = useSiteSettings('legal');
 *
 * To invalidate after an admin save:
 *   import { clearSiteSettingsCache } from '../hooks/useSiteSettings';
 *   clearSiteSettingsCache('services_hero');
 */
import { useState, useEffect } from 'react';
import API from '../utils/api';

// Simple in-memory cache so every component doesn't fire a fresh request
const _cache = {};

/** Call this after an admin saves a settings key so the next read re-fetches */
export const clearSiteSettingsCache = (key) => {
  if (key) {
    delete _cache[key];
  } else {
    Object.keys(_cache).forEach((k) => delete _cache[k]);
  }
};

const useSiteSettings = (key) => {
  const [data,    setData]    = useState(_cache[key] || null);
  const [loading, setLoading] = useState(!_cache[key]);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (_cache[key]) {
      setData(_cache[key]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await API.get(`/site-settings/public/${key}`);
        const val = r.data[key];
        _cache[key] = val;
        if (!cancelled) setData(val);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  return { data, loading, error };
};

export default useSiteSettings;